import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { StockStatusBadge } from './StockStatusBadge'
import { productsApi } from '../api/productsApi'
import { flattenVariantLeaves, type StockSizeNode } from '../utils/stockStatus'

interface VariantStockTableProps {
  sizes: StockSizeNode[] | undefined
}

/**
 * PE-2169 — per-variant stock table (size · material · colour · qty · status)
 * with a "Waiting for restock (n)" row for every sold-out leaf, read from
 * `GET /variants/:id/waitlist`. The waitlist read is best-effort: a 404 or an
 * empty list renders nothing, so products predating the platform variant
 * mirror still show their stock.
 */
export const VariantStockTable = ({ sizes }: VariantStockTableProps) => {
  const leaves = useMemo(() => flattenVariantLeaves(sizes), [sizes])
  const soldOut = leaves.filter((l) => l.status === 'out-of-stock' && l.variantId)

  const waitlists = useQueries({
    queries: soldOut.map((leaf) => ({
      queryKey: ['variants', leaf.variantId, 'waitlist'],
      queryFn: () => productsApi.platform.getVariantWaitlist(leaf.variantId),
      staleTime: 60 * 1000,
      retry: false,
    })),
  })
  const waitingByVariant = new Map<string, number>()
  soldOut.forEach((leaf, i) => {
    const count = waitlists[i]?.data?.waitingCount ?? 0
    if (count > 0) waitingByVariant.set(leaf.variantId, count)
  })

  if (leaves.length === 0) {
    return <p className="text-sm text-muted-foreground">No variants yet</p>
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-muted-foreground border-b border-border">
            <th className="py-2 pr-3 font-medium">Size</th>
            <th className="py-2 pr-3 font-medium">Material</th>
            <th className="py-2 pr-3 font-medium">Colour</th>
            <th className="py-2 pr-3 font-medium text-right">Qty</th>
            <th className="py-2 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {leaves.map((leaf, i) => {
            const waiting = waitingByVariant.get(leaf.variantId)
            return (
              <tr key={`${leaf.variantId || 'leaf'}-${i}`} className="border-b border-border last:border-0 align-top">
                <td className="py-2 pr-3">{leaf.sizeName || '—'}</td>
                <td className="py-2 pr-3">{leaf.materialName || '—'}</td>
                <td className="py-2 pr-3">
                  <span className="inline-flex items-center gap-2">
                    {leaf.hexValue && (
                      <span
                        className="w-3 h-3 rounded-full border border-[#B8B5B6] inline-block"
                        style={{ backgroundColor: leaf.hexValue }}
                      />
                    )}
                    {leaf.colourName || '—'}
                  </span>
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {leaf.quantity}
                  <span className="block text-xs text-muted-foreground">alert ≤ {leaf.stockUpTrigger}</span>
                </td>
                <td className="py-2">
                  <StockStatusBadge inStock={leaf.status} />
                  {waiting !== undefined && (
                    <span className="block text-xs text-muted-foreground mt-1">
                      Waiting for restock ({waiting})
                    </span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
